import * as React from 'react';
import { FormGroup } from 'react-bootstrap';
import type { SelectComponentsConfig, FormatOptionLabelMeta, OnChangeValue, Options, GroupBase } from 'react-select';
import type { WrappedFieldProps } from 'redux-form';

import AsyncSelect from 'af-components/Controls/AsyncSelect';

type DefaultOptionType = {
	label: string;
	value: number;
};

interface Props<OptionType = DefaultOptionType> extends WrappedFieldProps {
	placeholder?: string;
	className?: string;
	isClearable?: boolean;
	allowNew?: boolean;
	label: string;
	disabled?: boolean;
	components?: SelectComponentsConfig<OptionType, boolean, GroupBase<OptionType>>;
	grouped?: boolean;
	menuStyle?: React.CSSProperties;
	getOptionLabel?: (option: OptionType) => string;
	getOptionValue?: (option: OptionType) => string;
	onSearch: (inputValue: string, callback: (options: Options<OptionType>) => void) => Promise<OptionType[]>;
	onClear?: (selectedOption: OnChangeValue<OptionType, boolean>) => Promise<void> | void;
	onValueChange: (selectedOption: OnChangeValue<OptionType, boolean>) => Promise<void> | void;
	onBlur?: (selectedOption: OnChangeValue<OptionType, boolean>) => Promise<void> | void;
	onCreateNew?: (option: string) => Promise<void> | void;
	formatOptionLabel?: (option: OptionType, labelMeta: FormatOptionLabelMeta<OptionType>) => React.ReactNode;
	isValidNewOption?: (inputValue: string, value: OptionType, options: OptionType[]) => boolean;
	fixed?: boolean;
	defaultValue?: OptionType;
}

export default class SelectField<OptionType = DefaultOptionType> extends React.Component<Props<OptionType>> {
	static defaultProps: Partial<Props> = {
		grouped: true,
		fixed: false,
	};

	onChange = (selectedOption: OnChangeValue<OptionType, boolean>) => {
		const { input, onValueChange } = this.props;
		input.onChange(selectedOption);

		if (onValueChange) {
			onValueChange(selectedOption);
		}
	};

	handleBlur = () => {
		const { onBlur, value } = this.props.input;
		onBlur(value);
	};

	renderSelect = () => {
		const { input, defaultValue, ...rest } = this.props;
		return (
			<AsyncSelect
				{...rest}
				onBlur={this.handleBlur}
				onValueChange={this.onChange}
				value={!!input.value ? input.value : defaultValue}
			/>
		);
	};

	render() {
		const { grouped } = this.props;
		return grouped ? <FormGroup>{this.renderSelect()}</FormGroup> : this.renderSelect();
	}
}
